// ══════════════════════════════════════════════════════════
//  株価予測 × モンテカルロ ハイブリッド
//  段階1: LSTM予測（TF.js, CPU）→ μ・σ
//  段階2: モンテカルロ（gwaw-compute.js, GPU）→ 確率の帯
//  ※ import/export なし・グローバル関数（parcel build 対応）
// ══════════════════════════════════════════════════════════

// ── 状態管理 ──────────────────────────────────────────
let closeData = [];          // 終値の数値配列
let dataSource = 'syn';      // 'yf' | 'csv' | 'syn'
let lstmOutput = null;       // { futPred, lastActual, mu, sigma, rmse }
let mcOutput = null;         // simulatePaths の結果
let chartMain = null, chartFan = null;
const gc = new GwawCompute();

// ── バックエンド初期化（LSTM=CPU固定、MC=自動） ──────────
(async () => {
  try {
    await tf.setBackend('cpu');
    await tf.ready();
    document.getElementById('lstmBackend').textContent = 'LSTM: CPU';
  } catch(e) { document.getElementById('lstmBackend').textContent = 'LSTM: error'; }
  await gc.detect();
  document.getElementById('mcBackend').textContent = 'MC: ' + (gc.capabilities.webgpu ? 'WebGPU' : 'CPU');
})();

// ── ログ ─────────────────────────────────────────────
function log(msg, cls='') {
  const box = document.getElementById('logBox');
  const s = document.createElement('span');
  if (cls) s.className = cls;
  s.innerHTML = msg + '<br>';
  box.appendChild(s);
  box.scrollTop = box.scrollHeight;
}
function clearLog() { document.getElementById('logBox').innerHTML = ''; }

// ── データソース切替 ──────────────────────────────────
function switchDs(t) {
  dataSource = t;
  ['yf','csv','syn'].forEach(k => {
    const tab = document.getElementById('dsTab'+k.charAt(0).toUpperCase()+k.slice(1));
    if (tab) tab.classList.toggle('active', k===t);
    const panel = document.getElementById('dsPanel'+k.charAt(0).toUpperCase()+k.slice(1));
    if (panel) panel.style.display = (k===t) ? 'block' : 'none';
  });
}

// ── Yahoo Finance プロキシ ────────────────────────────
function buildProxies(symbol, range) {
  // 自サーバープロキシ（最優先・CORS問題なし）
  const selfProxy = 'https://www.iseeit.jp/tools/yf-proxy.php'
    + `?symbol=${encodeURIComponent(symbol)}&range=${encodeURIComponent(range)}`;
  // 外部プロキシ（フォールバック）
  const yfUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(symbol)}`
    + `?interval=1d&range=${range}&events=history`;
  const enc = encodeURIComponent(yfUrl);
  return [
    { name:'iseeit.jp proxy', url: selfProxy,                                                    parse: r=>r.json() },
    { name:'corsproxy.io',    url:`https://corsproxy.io/?${enc}`,                               parse: r=>r.json() },
    { name:'allorigins.win',  url:`https://allorigins.win/get?disableCache=true&url=${enc}`,    parse: async r=>{ const d=await r.json(); return JSON.parse(d.contents); } },
  ];
}
async function fetchYahoo() {
  const symbol = document.getElementById('yfSymbol').value.trim() || 'AAPL';
  const range  = document.getElementById('yfRange').value;
  log(`<span class="info">[FETCH]</span> ${symbol} (${range}) を取得中...`);
  const proxies = buildProxies(symbol, range);
  for (const url of proxies) {
    try {
      const res = await fetch(url.url);
      if (!res.ok) continue;
      const j = await res.json();
      const result = j.chart && j.chart.result && j.chart.result[0];
      if (!result) continue;
      const closes = result.indicators.quote[0].close.filter(v => v != null);
      if (closes.length < 50) continue;
      closeData = closes;
      log(`<span class="ok">[OK]</span> ${closes.length}日分のデータを取得`);
      drawMainChart();
      enableRun();
      return;
    } catch(e) { /* 次のプロキシ */ }
  }
  log(`<span class="err">[ERROR]</span> 取得失敗。CSVか合成データをお試しください`);
}

// ── CSV読込 ──────────────────────────────────────────
function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const closes = [];
  // 1行目がヘッダー（数値を含まない）なら飛ばす
  const firstCols = lines[0].split(',').map(c => c.replace(/^["']|["']$/g,'').trim());
  const startIdx = firstCols.some(c => !isNaN(parseFloat(c)) && c!=='') ? 0 : 1;
  for (let i=startIdx; i<lines.length; i++) {
    if (!lines[i].trim()) continue;
    const cols = lines[i].split(',').map(c => c.replace(/^["']|["']$/g,'').trim());
    // 最終列を終値とみなす（クォート除去済み）
    const v = parseFloat(cols[cols.length-1]);
    if (!isNaN(v)) closes.push(v);
  }
  return closes;
}
function onCsvFile(input) {
  const f = input.files[0]; if (!f) return;
  const reader = new FileReader();
  reader.onload = () => {
    const closes = parseCsv(reader.result);
    if (closes.length < 50) { log('<span class="err">[ERROR]</span> 50件以上のデータが必要です'); return; }
    closeData = closes;
    log(`<span class="ok">[OK]</span> CSV ${closes.length}件を読込`);
    drawMainChart(); enableRun();
  };
  reader.readAsText(f);
}

// ── 合成データ（デモ用） ──────────────────────────────
function generateSynthetic() {
  const n = 300;
  const trend = parseFloat(document.getElementById('synTrend').value);   // 年率ドリフト
  const vol   = parseFloat(document.getElementById('synVol').value);     // 年率ボラ
  let s = (Date.now() & 0xffffffff) >>> 0;
  const rnd = () => { s=(Math.imul(s,1664525)+1013904223)>>>0; return s/4294967296; };
  const g = () => { let u=rnd(),v=rnd(); if(u<1e-7)u=1e-7; return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };
  const prices = [100];
  const drift = (trend - vol*vol/2)/252, dvol = vol/Math.sqrt(252);
  for (let i=0;i<n;i++) prices.push(prices[prices.length-1]*Math.exp(drift+dvol*g()));
  closeData = prices;
  log(`<span class="ok">[OK]</span> 合成データ ${prices.length}日分を生成（トレンド${(trend*100).toFixed(0)}% ボラ${(vol*100).toFixed(0)}%）`);
  drawMainChart(); enableRun();
}

function enableRun() {
  const b = document.getElementById('runBtn'); if (b) b.disabled = false;
}

// LSTM学習中などにUIをまとめてロック/アンロック
function setBusy(busy) {
  const ids = ['dsTabSyn','dsTabCsv','dsTabYf','synTrend','synVol','synBtn',
               'csvFile','yfSymbol','yfRange','yfBtn','lstmWin','runBtn','mcRunBtn'];
  ids.forEach(id => { const el = document.getElementById(id); if (el) el.disabled = busy; });
}

// ── 正規化・系列作成 ──────────────────────────────────
function normalize(arr) {
  const mn=Math.min(...arr), mx=Math.max(...arr), rng=mx-mn||1;
  return { norm:arr.map(v=>(v-mn)/rng), min:mn, max:mx, range:rng };
}
function denorm(v, min, range) { return v*range+min; }
function makeSequences(normed, win) {
  const X=[], Y=[];
  for(let i=win;i<normed.length;i++){ X.push(normed.slice(i-win,i).map(v=>[v])); Y.push(normed[i]); }
  return {X,Y};
}

// ── μ・σ 橋渡し（案B：μ=LSTM予測トレンド、σ=ヒストリカルボラ） ──
function historicalVol(prices) {
  const rets=[];
  for(let i=1;i<prices.length;i++) rets.push(Math.log(prices[i]/prices[i-1]));
  const mean=rets.reduce((a,b)=>a+b,0)/rets.length;
  const varr=rets.reduce((a,b)=>a+(b-mean)**2,0)/Math.max(1,rets.length-1);
  return Math.sqrt(varr)*Math.sqrt(252); // 年率
}
function muFromPrediction(futPred, lastActual) {
  // 予測期間全体の累積対数リターンを年率化（日次平均×252は過剰増幅のため避ける）
  const horizon = futPred.length;
  if (horizon < 1) return { mu:0, raw:0, clipped:false };
  const totalLogRet = Math.log(futPred[futPred.length-1] / lastActual);
  const raw = totalLogRet * (252 / horizon);
  // 再帰予測のドリフト暴走を防ぐ安全弁（±40%/年でクリップ）
  const CAP = 0.4;
  const mu = Math.max(-CAP, Math.min(CAP, raw));
  return { mu, raw, clipped: Math.abs(raw) > CAP };
}

// ── 段階1：LSTM学習・予測（TF.js, CPU） ──────────────
async function runLstm() {
  if (closeData.length < 50) { log('<span class="err">[ERROR]</span> データを先に読み込んでください'); return; }
  const win   = parseInt(document.getElementById('lstmWin').value);
  const predDays = parseInt(document.getElementById('mcDays').value); // MC期間と合わせLSTMトレンド推定日数
  const epochs = 25, batchSize = 16, trainRatio = 0.8;

  log(`<span class="info">[LSTM]</span> 学習開始 win=${win} epochs=${epochs}（CPU）`);
  setBusy(true);
  try {

  const { norm, min:pMin, range:pRange } = normalize(closeData);
  const { X, Y } = makeSequences(norm, win);
  const trainSize = Math.floor(X.length * trainRatio);
  const xTrain = tf.tensor3d(X.slice(0, trainSize));
  const yTrain = tf.tensor2d(Y.slice(0, trainSize), [trainSize, 1]);
  const xTest  = tf.tensor3d(X.slice(trainSize));

  const model = tf.sequential();
  model.add(tf.layers.lstm({ units:48, returnSequences:true, inputShape:[win,1] }));
  model.add(tf.layers.dropout({ rate:0.2 }));
  model.add(tf.layers.lstm({ units:24, returnSequences:false }));
  model.add(tf.layers.dropout({ rate:0.2 }));
  model.add(tf.layers.dense({ units:1 }));
  model.compile({ optimizer:tf.train.adam(0.001), loss:'meanSquaredError' });

  const t0 = performance.now();
  await model.fit(xTrain, yTrain, {
    epochs, batchSize, verbose:0,
    callbacks:{ onEpochEnd:(ep,logs)=>{ if((ep+1)%5===0) log(`  epoch ${ep+1}/${epochs}  loss=${logs.loss.toFixed(5)}`,'dim'); } }
  });
  const lstmTime = performance.now() - t0;

  // テスト誤差（RMSE）
  const yPredNorm = Array.from(model.predict(xTest).dataSync());
  const yTestActual = Y.slice(trainSize).map(v => denorm(v, pMin, pRange));
  const yPredActual = yPredNorm.map(v => denorm(v, pMin, pRange));
  const rmse = Math.sqrt(yPredActual.reduce((s,v,i)=>s+(v-yTestActual[i])**2,0)/Math.max(1,yPredActual.length));

  // 多ステップ予測（予測トレンド推定用）
  let seq = norm.slice(norm.length-win).map(v=>[v]);
  const futPred = [];
  for (let s=0; s<predDays; s++) {
    const t = tf.tensor3d([seq]);
    const pv = model.predict(t).dataSync()[0];
    futPred.push(denorm(pv, pMin, pRange));
    seq = [...seq.slice(1), [pv]];
    t.dispose();
  }
  xTrain.dispose(); yTrain.dispose(); xTest.dispose(); model.dispose();

  const lastActual = closeData[closeData.length-1];
  const muResult = muFromPrediction(futPred, lastActual);
  const mu = muResult.mu;
  const sigma = historicalVol(closeData);
  lstmOutput = { futPred, lastActual, mu, sigma, rmse, lstmTime };

  log(`<span class="ok">[LSTM完了]</span> ${lstmTime.toFixed(0)}ms / RMSE=${rmse.toFixed(3)}`);
  log(`  → 予測トレンド μ=${(mu*100).toFixed(1)}%/年, ヒストリカル σ=${(sigma*100).toFixed(1)}%/年`,'info');
  if (muResult.clipped) {
    log(`  ※ 予測トレンドが極端なため ±40%/年 で制限しました（元の値: ${(muResult.raw*100).toFixed(1)}%/年）。再帰予測は下落方向にドリフトしやすく、実データでは下げ予測に偏りがちです`,'dim');
  }
  document.getElementById('muVal').textContent = (mu*100).toFixed(1)+'%';
  document.getElementById('sigmaVal').textContent = (sigma*100).toFixed(1)+'%';
  document.getElementById('paramCard').style.display='block';
  drawMainChart(futPred);
  } catch (e) {
    log(`<span class="err">[ERROR]</span> LSTM実行中にエラー: ${e.message}`);
  } finally {
    setBusy(false);
    // MC実行ボタンは、LSTM予測が成立したときのみ有効
    document.getElementById('mcRunBtn').disabled = (lstmOutput === null);
  }
}

// ── 段階2：モンテカルロ（GPU）→ 確率の帯 ─────────────
async function runMC() {
  if (!lstmOutput) { log('<span class="err">[ERROR]</span> 先にLSTM予測を実行してください'); return; }
  const days = parseInt(document.getElementById('mcDays').value);
  const N = parseInt(document.getElementById('mcN').value);
  const be = gc.capabilities.webgpu ? 'webgpu' : 'cpu';
  const mcBtn = document.getElementById('mcRunBtn');
  mcBtn.disabled = true;
  log(`<span class="info">[MC]</span> ${N.toLocaleString()}本の経路を生成（${be}）`);

  try {
  const r = await gc.simulatePaths({
    S0: lstmOutput.lastActual, mu: lstmOutput.mu, sigma: lstmOutput.sigma,
    steps: days, dt: 1/252, N, backend: be
  });
  mcOutput = r;
  log(`<span class="ok">[MC完了]</span> ${r.elapsed.toFixed(0)}ms / ${r.backend}`);
  const final = r.steps[r.steps.length-1];
  log(`  → ${days}日後：中央値 ${final.vals[2].toFixed(1)}, 90%区間 ${final.vals[0].toFixed(1)}〜${final.vals[4].toFixed(1)}`,'info');

  // ハイブリッドの実行時間比較
  log(`<span class="dim">[時間比較] LSTM(CPU) ${lstmOutput.lstmTime.toFixed(0)}ms ／ MC(${r.backend}) ${r.elapsed.toFixed(0)}ms</span>`);

  drawFanChart(r);
  } catch (e) {
    log(`<span class="err">[ERROR]</span> モンテカルロ実行中にエラー: ${e.message}`);
  } finally {
    mcBtn.disabled = false;
  }
}

// ── メインチャート（実測＋LSTM点予測） ────────────────
function drawMainChart(futPred) {
  const ctx = document.getElementById('mainChart');
  const labels = closeData.map((_,i)=>i);
  const datasets = [{ label:'実測株価', data:closeData, borderColor:'#22e0ff', backgroundColor:'rgba(34,224,255,.07)', borderWidth:2.5, pointRadius:0, fill:true, tension:.1 }];
  if (futPred && futPred.length) {
    const pad = new Array(closeData.length-1).fill(null);
    datasets.push({ label:'LSTM予測', data:[...pad, closeData[closeData.length-1], ...futPred], borderColor:'#ffd93d', backgroundColor:'transparent', borderWidth:2.5, borderDash:[6,3], pointRadius:0, tension:.1 });
  }
  if (chartMain) chartMain.destroy();
  chartMain = new Chart(ctx, {
    type:'line', data:{ labels:futPred?labels.concat(futPred.map((_,i)=>labels.length+i)):labels, datasets },
    options:{ responsive:true, maintainAspectRatio:false,
      plugins:{ legend:{ labels:{ color:'#9fb0c8', font:{size:11} } } },
      scales:{ x:{ ticks:{color:'#5a6a82',maxTicksLimit:8,font:{size:10}}, grid:{color:'#222838'}, title:{display:true,text:'日数',color:'#7a8a9e'} },
        y:{ ticks:{color:'#5a6a82',font:{size:10}}, grid:{color:'#222838'}, title:{display:true,text:'価格',color:'#7a8a9e'} } } }
  });
  requestAnimationFrame(()=>requestAnimationFrame(()=>{ if(chartMain)chartMain.resize(); }));
}

// ── ファンチャート（MC確率の帯） ──────────────────────
function drawFanChart(r) {
  document.getElementById('fanCard').style.display='block';
  const labels = r.steps.map(s=>s.t);
  if (chartFan) chartFan.destroy();
  chartFan = new Chart(document.getElementById('fanChart'), {
    type:'line',
    data:{ labels, datasets:[
      { label:'5%', data:r.steps.map(s=>s.vals[0]), borderColor:'rgba(255,107,53,.4)', backgroundColor:'transparent', pointRadius:0, borderWidth:1, tension:.1 },
      { label:'b1', data:r.steps.map(s=>s.vals[1]), borderColor:'transparent', backgroundColor:'rgba(0,212,255,.10)', fill:'-1', pointRadius:0, tension:.1 },
      { label:'b2', data:r.steps.map(s=>s.vals[2]), borderColor:'transparent', backgroundColor:'rgba(0,212,255,.18)', fill:'-1', pointRadius:0, tension:.1 },
      { label:'中央値', data:r.steps.map(s=>s.vals[2]), borderColor:'#22e0ff', backgroundColor:'transparent', pointRadius:0, borderWidth:2.5, tension:.1 },
      { label:'b3', data:r.steps.map(s=>s.vals[3]), borderColor:'transparent', backgroundColor:'rgba(0,212,255,.18)', fill:'-1', pointRadius:0, tension:.1 },
      { label:'b4', data:r.steps.map(s=>s.vals[4]), borderColor:'transparent', backgroundColor:'rgba(0,212,255,.10)', fill:'-1', pointRadius:0, tension:.1 },
      { label:'95%', data:r.steps.map(s=>s.vals[4]), borderColor:'rgba(57,255,20,.5)', backgroundColor:'transparent', pointRadius:0, borderWidth:1.5, tension:.1 },
    ]},
    options:{ responsive:true, maintainAspectRatio:false,
      plugins:{ legend:{ labels:{ color:'#9fb0c8', font:{size:10}, filter:(it)=>['5%','中央値','95%'].includes(it.text) } } },
      scales:{ x:{ ticks:{color:'#5a6a82',maxTicksLimit:8,font:{size:10}}, grid:{color:'#222838'}, title:{display:true,text:'日数（将来）',color:'#7a8a9e'} },
        y:{ ticks:{color:'#5a6a82',font:{size:10}}, grid:{color:'#222838'}, title:{display:true,text:'価格',color:'#7a8a9e'} } } }
  });
  const final = r.steps[r.steps.length-1];
  document.getElementById('fanInfo').innerHTML =
    `LSTMの点予測（黄）が「最もありそうな1本」、MCの帯が「起こりうる範囲」。<br>${r.steps[r.steps.length-1].t}日後は、90%の確率で <strong style="color:#00d4ff">${final.vals[0].toFixed(0)}〜${final.vals[4].toFixed(0)}</strong> に収まる見込みです。`;
  requestAnimationFrame(()=>requestAnimationFrame(()=>{ if(chartFan)chartFan.resize(); }));
}

document.getElementById("dsTabYf").addEventListener("click", () => {
  switchDs('yf');
});
document.getElementById("dsTabCsv").addEventListener("click", () => {
  switchDs('csv');
});
document.getElementById("dsTabSyn").addEventListener("click", () => {
  switchDs('syn');
});
document.getElementById("yfBtn").addEventListener("click", fetchYahoo);
document.getElementById("csvFile").addEventListener("change", (event) => {
  onCsvFile(event.target);
});
document.getElementById("synBtn").addEventListener("click", generateSynthetic);
document.getElementById("runBtn").addEventListener("click", runLstm);
document.getElementById("mcRunBtn").addEventListener("click", runMC);